import { useToast } from '@repo/design-system/index'
import { differenceInDays, isAfter, parseISO } from 'date-fns'
import { useEffect, useState } from 'react'
import {
  useChallengeApply,
  useFetchChallenge,
  useFetchMyChallengeStatus,
  useRemoveChallenge,
} from '../../../../modules/apis/student/challenge'
import { useCustomerType } from '../../../../modules/apis/student/products'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_silcon } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'

const challengeTargets = {
  20: {
    totalTargetCount: 36,
    pointInterval: 9,
  },
  40: {
    totalTargetCount: 24,
    pointInterval: 6,
  },
}

const getChallengeProgress = (challengeProgressData, lessonDuration) => {
  const progressByDuration = challengeProgressData?.find(
    (progress) => progress.user_challenge_program.lesson_duration === lessonDuration
  )

  const currentChallenge = challengeTargets[lessonDuration]
  const completedCount = progressByDuration?.stamps?.length || 0
  const totalTargetCount = currentChallenge.totalTargetCount
  const achievementRate = totalTargetCount > 0 ? Math.round((completedCount / totalTargetCount) * 100) : 0
  const isCompleted = completedCount >= totalTargetCount

  const nextRewardPoints = Math.min((Math.floor(completedCount / currentChallenge.pointInterval) + 1) * 6000, 24000)

  return {
    ...progressByDuration,
    completedCount,
    totalTargetCount,
    achievementRate,
    isCompleted,
    nextRewardPoints,
    maxRewardPoints: 24000,
  }
}

const getLessonDurationWithLatestStamp = (challengeProgress) => {
  if (!challengeProgress) return null

  const allStamps = challengeProgress
    .filter((progress) => Array.isArray(progress.stamps) && progress.stamps.length > 0)
    .flatMap((progress) =>
      progress.stamps
        .filter((stamp) => typeof stamp.start_time === 'string' && !isNaN(new Date(stamp.start_time).getTime()))
        .map((stamp) => ({
          startTime: new Date(stamp.start_time),
          lessonDuration: progress.user_challenge_program.lesson_duration,
        }))
    )

  if (allStamps.length === 0) return null

  const latestStamp = allStamps.reduce((latest, current) => (current.startTime > latest.startTime ? current : latest))

  return latestStamp?.lessonDuration ?? null
}

const useChallenge = () => {
  const [selectedDuration, setSelectedDuration] = useState(40)
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)

  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const getFullUrl = useReturnFullUrl()
  const { t } = useTranslation()
  const { toastSuccess, toastError } = useToast()

  const { data: challengeData, isFetching: isChallengeDataFetching } = useFetchChallenge()
  const { mutate: applyChallenge } = useChallengeApply()
  const { mutate: removeChallenge } = useRemoveChallenge()
  const { data: myChallengeProgressData } = useFetchMyChallengeStatus({
    enabled: !!currentUser && challengeData?.registered,
  })
  const { data: customerType } = useCustomerType()
  const isB2CEligible = customerType?.isB2CEligible

  const challengeInfo = {
    startTime: challengeData?.challenge_program?.start_time,
    remainingDays: challengeData?.challenge_program?.end_time
      ? differenceInDays(new Date(challengeData.challenge_program.end_time), new Date())
      : 0,
    registrationCount: challengeData?.challenge_program?.count_registration,
    isRegistered: challengeData?.registered,
    isStarted: challengeData?.challenge_program?.start_time
      ? isAfter(new Date(), parseISO(challengeData.challenge_program.start_time))
      : false,
  }
  const challengeProgressData = myChallengeProgressData?.challenge_progress
  const challengeProgress20Min = getChallengeProgress(challengeProgressData, 20)
  const challengeProgress40Min = getChallengeProgress(challengeProgressData, 40)
  const challengeProgress = selectedDuration === 20 ? challengeProgress20Min : challengeProgress40Min

  const isRingleMember = currentUser?.is_ringle_member
  const uuid = challengeData?.challenge_program?.uuid

  const navigateToChallenge = () => {
    const webUrl = '/student/landing/challenge/silicon-valley'
    const webviewUrl = '/webview/home?activity=SiliconValleyChallengeActivity'

    router.push(webUrl, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(webviewUrl) },
    })
  }

  const handleChallengeApply = async () => {
    if (!currentUser) {
      router.push(`${urls.student.auth.signIn}?redirect_url=${router.asPath}`)
      return
    }

    if (!uuid) return

    if (!isB2CEligible) {
      alert('회사를 통해 수강하시는 경우에는 참여가 불가합니다.참여를 희망하실 경우, 개인 계정으로 참여해주세요!')
      return
    }

    setIsApplyModalOpen(true)
  }

  const handleChallengeApplyConfirm = () => {
    applyChallenge(uuid, {
      onSuccess: (data) => {
        if (data.success) {
          toastSuccess(t(n_silcon.toast.success))
        } else {
          toastError(data.message)
        }
      },
      onSettled: () => {
        setIsApplyModalOpen(false)
      },
      onError: () => {
        alert('Failed to apply for the challenge!')
      },
    })
  }

  const handleRemoveChallenge = () => {
    if (!uuid) return

    removeChallenge(uuid, {
      onSuccess: () => {
        toastSuccess('Successfully canceled the challenge')
      },
      onError: () => {
        alert('Failed to cancel the challenge')
      },
    })
  }

  useEffect(() => {
    const latestLessonDuration = getLessonDurationWithLatestStamp(myChallengeProgressData?.challenge_progress)
    if (latestLessonDuration) {
      setSelectedDuration(latestLessonDuration)
    }
  }, [myChallengeProgressData])

  return {
    isChallengeDataFetching,
    selectedDuration,
    challengeInfo,
    challengeProgress,
    isApplyModalOpen,
    setSelectedDuration,
    setIsApplyModalOpen,
    navigateToChallenge,
    handleChallengeApply,
    handleChallengeApplyConfirm,
    handleRemoveChallenge,
    isRingleMember,
  }
}

export default useChallenge
